import * as React from "react";

import {
  Alert,
  Button,
  Card as KitCard,
  controlClasses,
  EmptyState as KitEmptyState,
  FormField,
  Input,
  Skeleton,
  Spinner,
  Switch as KitSwitch,
  type ButtonProps,
} from "Components/ui";
import { cn } from "utils/cn";

// Billing's original building blocks, now thin adapters over the shared UI kit
// (Components/ui) so the billing, pay and client-modules pages keep their props but get the
// kit's look. New code should import from Components/ui directly.

type ButtonVariant = "primary" | "secondary" | "danger" | "dangerOutline" | "ghost";
type ButtonSize = "xs" | "sm" | "md" | "lg";

const KIT_VARIANT: Record<ButtonVariant, ButtonProps["variant"]> = {
  primary: "primary",
  secondary: "secondary",
  danger: "danger",
  dangerOutline: "danger-outline",
  ghost: "primary-ghost",
};

export interface ActionButtonProps {
  variant?: ButtonVariant;
  size?: ButtonSize;
  type?: "button" | "submit";
  disabled?: boolean;
  loading?: boolean;
  icon?: React.ReactNode;
  title?: string;
  className?: string;
  ariaLabel?: string;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  children?: React.ReactNode;
}

export const ActionButton: React.FC<ActionButtonProps> = ({
  variant = "secondary",
  size = "md",
  icon,
  ariaLabel,
  children,
  ...props
}) => (
  <Button
    variant={KIT_VARIANT[variant]}
    size={size}
    leftIcon={icon}
    aria-label={ariaLabel}
    {...props}
  >
    {children}
  </Button>
);

export const inputClass = (hasError = false, extra = ""): string =>
  cn(controlClasses, "h-10 px-3", hasError && "border-danger focus-visible:ring-danger/15", extra);

interface FieldProps {
  label: React.ReactNode;
  htmlFor?: string;
  hint?: React.ReactNode;
  error?: string | null;
  required?: boolean;
  className?: string;
  children?: React.ReactNode;
}

export const Field: React.FC<FieldProps> = ({ htmlFor, error, children, ...props }) => (
  <FormField id={htmlFor} error={error || undefined} {...props}>
    {children}
  </FormField>
);

interface CardProps {
  title?: React.ReactNode;
  description?: React.ReactNode;
  actions?: React.ReactNode;
  className?: string;
  bodyClassName?: string;
  children?: React.ReactNode;
}

export const Card: React.FC<CardProps> = ({
  title,
  description,
  actions,
  className,
  bodyClassName = "px-5 py-4",
  children,
}) => (
  <KitCard className={className}>
    {(title || actions) && (
      <header className="flex flex-col gap-3 border-b border-border px-5 py-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          {title && (
            <h3 className="text-base font-semibold tracking-tight text-foreground">{title}</h3>
          )}
          {description && <p className="mt-0.5 text-sm text-muted-foreground">{description}</p>}
        </div>
        {actions && <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>}
      </header>
    )}
    <div className={bodyClassName}>{children}</div>
  </KitCard>
);

type BannerTone = "info" | "warning" | "danger" | "success" | "neutral";

interface BannerProps {
  tone?: BannerTone;
  title?: React.ReactNode;
  icon?: React.ReactNode;
  actions?: React.ReactNode;
  className?: string;
  children?: React.ReactNode;
}

export const Banner: React.FC<BannerProps> = ({ actions, ...props }) => (
  <Alert action={actions} {...props} />
);

interface SwitchProps {
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
  id?: string;
  label: string;
}

export const Switch: React.FC<SwitchProps> = ({
  checked,
  onChange,
  disabled = false,
  id,
  label,
}) => (
  <KitSwitch
    id={id}
    checked={checked}
    onCheckedChange={onChange}
    disabled={disabled}
    aria-label={label}
  />
);

interface EmptyStateProps {
  icon?: React.ReactNode;
  title: string;
  description?: React.ReactNode;
  action?: React.ReactNode;
  className?: string;
}

export const EmptyState: React.FC<EmptyStateProps> = (props) => (
  <KitEmptyState compact {...props} />
);

export const LoadingBlock: React.FC<{ label?: string; className?: string }> = ({
  label = "Loading…",
  className,
}) => (
  <div
    className={cn(
      "flex items-center justify-center gap-2 px-4 py-10 text-sm text-muted-foreground",
      className
    )}
  >
    <Spinner className="h-5 w-5" />
    {label}
  </div>
);

export const SkeletonRows: React.FC<{ rows?: number }> = ({ rows = 4 }) => (
  <div className="space-y-3 px-5 py-4" aria-hidden="true">
    {Array.from({ length: rows }, (_, index) => (
      <Skeleton key={index} className="h-10" />
    ))}
  </div>
);

export interface DetailItem {
  label: React.ReactNode;
  value: React.ReactNode;
  hint?: React.ReactNode;
}

export const DetailGrid: React.FC<{ items: DetailItem[]; columns?: 2 | 3 }> = ({
  items,
  columns = 2,
}) => (
  <dl
    className={cn(
      "grid grid-cols-1 gap-3",
      columns === 3 ? "sm:grid-cols-2 lg:grid-cols-3" : "sm:grid-cols-2"
    )}
  >
    {items.map((item, index) => (
      <div
        key={index}
        className="rounded-control border border-border bg-surface-muted/60 px-4 py-3"
      >
        <dt className="text-xs font-medium text-muted-foreground">{item.label}</dt>
        <dd className="mt-1 break-words text-sm text-foreground">{item.value}</dd>
        {item.hint && <dd className="mt-1 text-xs text-muted-foreground">{item.hint}</dd>}
      </div>
    ))}
  </dl>
);

/** The browser's date picker in a kit Input, typed for "YYYY-MM-DD" values. */
export const DateInput: React.FC<{
  id?: string;
  value: string;
  onChange: (value: string) => void;
  min?: string;
  max?: string;
  disabled?: boolean;
  hasError?: boolean;
}> = ({ id, value, onChange, min, max, disabled = false, hasError = false }) => (
  <Input
    id={id}
    type="date"
    value={value}
    min={min}
    max={max}
    disabled={disabled}
    aria-invalid={hasError || undefined}
    onChange={(event) => onChange(event.target.value)}
  />
);
